import { Alert } from '@mui/material';
import axios from 'axios';
import {capitalize, get, includes, isEmpty} from 'lodash'
import {
  FC,
  PropsWithChildren,
  createContext,
  useEffect,
  useState,
  useCallback,
} from 'react'
import toast from 'react-hot-toast'
import {jwtDecode} from "jwt-decode";
import Axios from '../axios'
import Auth from '../interfaces/Auth'
import User from '../interfaces/User'
import * as keycloak from '../keycloak'
import { getPosToken, getUser } from '../services/user'
import CasaLoader from '../shared/components/CasaLoader/CasaLoader'
import { UserProvider } from './User'
import {posRoutePages} from "../routes";
import { doesRouteTokenAuthenticated } from '../services/auth'
import { saveConfig } from '../utils/indexDB';
import { updateTenant } from '../features/TenantSelection/services/api';
import { broadcastTenantChange, initTenantSync } from '../utils/tenantSync';

const AuthContext = createContext<Auth>({
  currentUser: null,
  updateUserTenant: async () => {},
});

export const AuthProvider: FC<PropsWithChildren> = ({children}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [isRouteAuthenticated, setIsRouteAuthenticated] = useState(false)
  const [currentUser, setCurrentUser] = useState<User>()
  const [error, setError] = useState<string | null>(null)

  const isPosRoute = useCallback((pathName: string) => {
    for (const route of posRoutePages) {
      if (route.path === pathName) {
        return true;
      }
    }
    return false
  }, [])

  // Setup cross-tab tenant change listener
  useEffect(() => {
    const cleanup = initTenantSync(() => {
      // Another tab switched tenant 
      console.log('Reloading due to tenant change in another tab');
      window.location.reload();
    });

    return cleanup;
  }, []);

  // Function to update tenant and refresh user
  const updateUserTenant = useCallback(async (tenantId: number) => {
    try {
      await updateTenant(tenantId);      
      broadcastTenantChange();      
      const user = await getUser();
      setCurrentUser(user);
      saveConfig("notification", { soundEnabled: user.notification_sound_enabled });
      toast.success(`Switched to ${capitalize(user.tenant.displayName)}`);
      window.location.reload();
    } catch (error) {
      console.error('Failed to update tenant:', error);
      toast.error('Tenant switch failed');
      // throw error;
    }
  }, []);

  // Initial authentication and user fetch
  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);
      try {
        const currentPath = window.location.pathname;
        
        // Handle POS route authentication
        if (isPosRoute(currentPath) && includes(window.location.href, "loadTokenFromUrl")) {
          const urlSearchParams = new URLSearchParams(window.location.search);
          const decoded: string | null = urlSearchParams.get("token");
          if (decoded !== null) {
            const token = await getPosToken(decoded)
              .then((response) => {
                const token = get(response, "token");
                const decoded = jwtDecode(token);
                return get(decoded, 'token')
              })
            if (token !== undefined) {
              Axios.init({ token: token });
              const user = await getUser();
              saveConfig("notification", { soundEnabled: user.notification_sound_enabled });
              setCurrentUser(user);
              return;
            }
          }
        }
        
        // Handle route-based authentication
        const doesRouteAuthenticated = doesRouteTokenAuthenticated()
        if (doesRouteAuthenticated) {
          setIsRouteAuthenticated(true);
          return;
        }

        const isAuthenticated = await keycloak.initialize();

        if (!isAuthenticated) {
          keycloak.redirectToLogin();
          return;
        }

        const accessToken = keycloak.getAccessToken();

        if (!accessToken) {
          throw new Error('Authentication failed');
        }

        axios.defaults.headers.common[
          'Authorization'
        ] = `Bearer ${accessToken}`;
        
        // Fetch user without tenantId - backend returns user with their selected tenant
        const user = await getUser();
        saveConfig("notification", { soundEnabled: user.notification_sound_enabled });
        setCurrentUser(user);

        toast.success(`Welcome back, ${capitalize(user.tenant.displayName)}`);
      } catch (error) {
        console.log("error", error);
        setError((error as Error)?.message);
        toast.error(`Oops! Something went wrong`);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [isPosRoute]);

  if (isLoading) return <CasaLoader />
  if (error) return <Alert severity="error">{error}</Alert>
  if (isRouteAuthenticated) return <>{children}</>
  if (isEmpty(currentUser))
    return <Alert severity="error">Oops! Something went wrong</Alert>


  return (
    <AuthContext.Provider value={{ currentUser, updateUserTenant }}>
      <UserProvider currentUser={currentUser}>{children}</UserProvider>
    </AuthContext.Provider>
  )
}

export default AuthContext